import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import type { SourceBreakdownEntryShape } from "../../types/discount-analytics-types";

interface Props {
  rows: SourceBreakdownEntryShape[];
}

const SOURCE_COLOR: Record<string, string> = {
  google: "bg-blue-500",
  facebook: "bg-blue-700",
  instagram: "bg-pink-500",
  email: "bg-amber-500",
  direct: "bg-slate-500",
};

function colorFor(source: string): string {
  return SOURCE_COLOR[source.toLowerCase()] ?? "bg-emerald-500";
}

function formatPercent(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

export function SourceBreakdown({ rows }: Props) {
  const { t } = useTranslation("reports");

  if (rows.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="h-4 w-4 text-slate-500" />
            {t("discounts.sourceBreakdown.title")}
          </CardTitle>
          <CardDescription>{t("discounts.sourceBreakdown.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground italic">
            {t("discounts.sourceBreakdown.emptyState")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const maxRedemption = Math.max(...rows.map((r) => r.redemptionCount));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Globe className="h-4 w-4 text-slate-500" />
          {t("discounts.sourceBreakdown.title")}
        </CardTitle>
        <CardDescription>{t("discounts.sourceBreakdown.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {rows.map((row) => {
            const widthPercent = (row.redemptionCount / maxRedemption) * 100;
            return (
              <div key={row.source} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium capitalize">{row.source}</span>
                  <span className="text-muted-foreground tabular-nums">{row.redemptionCount}</span>
                </div>
                <div className="relative h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className={cn("absolute inset-y-0 left-0 rounded-full", colorFor(row.source))}
                    style={{ width: `${widthPercent}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("discounts.sourceBreakdown.columns.source")}</TableHead>
              <TableHead className="text-right">
                {t("discounts.sourceBreakdown.columns.redemptions")}
              </TableHead>
              <TableHead className="text-right">
                {t("discounts.sourceBreakdown.columns.amount")}
              </TableHead>
              <TableHead className="text-right">
                {t("discounts.sourceBreakdown.columns.conversionRate")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.source}>
                <TableCell className="font-medium capitalize">{row.source}</TableCell>
                <TableCell className="text-right tabular-nums">{row.redemptionCount}</TableCell>
                <TableCell className="text-right tabular-nums">
                  <PriceDisplay amount={row.totalAmountDiscounted} />
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.conversionRate > 0 ? formatPercent(row.conversionRate) : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
